import { Button, Icon, IconButton, Input, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Text, Pressable, Flex } from 'native-base';
import React from 'react';
import { Control } from 'react-hook-form';
import { useRecoilValue } from 'recoil';
import { v4 as uuid } from 'uuid';

import { tagsState } from '../../../../../../store/batchTicket/atom';
import { useCustomerTagContent } from '../../../../hooks';
import { tagSchemaData } from '../../../../hooks/constants';
import { CustomerItemTagObject } from '../../../../utils/types';

import { ItemTagProps } from '.';

interface SelectableTagItemProps {
  isSelected: boolean;
  item: CustomerItemTagObject;
  onAdd: () => void;
  onEdit: () => void;
  isListing?: boolean;
}

const gapWrapperStyle = { gap: 8 };

//Box para selecionar tags ja criadas
const SelectableTagItem: React.FC<SelectableTagItemProps> = ({
  isSelected,
  onAdd,
  onEdit,
  item,
  isListing,
}) => {
  const textColor = isSelected ? 'brand.tertiary.dark' : 'neutral.light';
  const bgColor = isSelected ? 'feedback.gen.lightest' : 'neutral.lightest';
  const iconColor = Color[isSelected ? 'brand.tertiary.dark' : 'neutral.light'];

  return (
    <Pressable onPress={!isListing ? onAdd : null} maxW={{ base: 'full', lg: '236px' }}>
      <HStack
        bgColor={bgColor}
        alignItems="center"
        space={Spacing['inline.nano']}
        borderRadius={BoxRadius.xs}
        px={Spacing['inline.nano']}
        py={Spacing['inline.quarck']}>
        <Text
          maxW={{ lg: '200px' }}
          color={textColor}
          variant="semibold"
          fontSize={{ base: Typography['fontsize.lg'], lg: Typography['fontsize.md'] }}>
          {item.tagName}
        </Text>
        {!isListing && (
          <IconButton
            testId="edit-tag-icon-button"
            p={0}
            onPress={onEdit}
            iconColor={iconColor}
            icon="EditPen"
            iconSize="xxs"
          />
        )}
      </HStack>
    </Pressable>
  );
};

export const ItemTagContent: React.FC<ItemTagProps> = ({
  data: customerTags,
  itemId,
  isListing = false,
}) => {
  const {
    createNewTagIsValid,
    inputProps,
    itemEdit,
    mode,
    control,
    errors,
    loading,
    handleEditTag,
    handleConfirmRemove,
    handleCreateOrEditTag,
    handleReturnToAddMode,
    handleSubmit,
    handleLinkUnlinkTag,
  } = useCustomerTagContent({ itemId });

  const tags = useRecoilValue(tagsState);
  const tagList = isListing ? customerTags : tags?.list;

  const handleLinkAndCreateOrEditTag = ({ tagValue }: tagSchemaData) => {
    if (mode === 'edit') {
      const data = { tagId: itemEdit.tagId, tagName: tagValue };

      handleCreateOrEditTag(data);
    }

    if (mode === 'add') {
      const data = { tagId: uuid(), tagName: tagValue };

      handleCreateOrEditTag(data);
    }
  };

  const tagItemList = tagList.map((item) => {
    const isSelected = customerTags.some((customerTag) => customerTag.tagId === item.tagId);
    return (
      <SelectableTagItem
        key={item.tagId}
        item={item}
        isSelected={isSelected}
        onAdd={() => handleLinkUnlinkTag(item, isSelected)}
        onEdit={() => handleEditTag(item)}
        isListing={isListing}
      />
    );
  });
  const TagEditMode = (
    <HStack alignItems="center" justifyContent={'space-between'}>
      <Button
        variant={'textLink'}
        p={0}
        isLoading={loading}
        isDisabled={loading}
        onPress={() => handleConfirmRemove(itemEdit.tagId)}
        rightIcon={<Icon color={Color['feedback.error.default']} name="Trash" size="xxxs" />}>
        <Text
          variant="semibold"
          fontSize={Typography['fontsize.xs']}
          // onPress={() => handleConfirmRemove(itemEdit.tagId)}
          color="feedback.error.default">
          DELETAR TAG
        </Text>
      </Button>
      <Button
        variant={'textLink'}
        p={0}
        onPress={handleReturnToAddMode}
        isDisabled={loading}
        isLoading={loading}>
        <Text
          variant="semibold"
          fontSize={Typography['fontsize.xs']}
          color="brand.tertiary.principal">
          CANCELAR
        </Text>
      </Button>
    </HStack>
  );

  return (
    <VStack space={Spacing['stack.xxxs']} w="full" maxH={400} overflow="auto">
      {!isListing && (
        <Input
          testId="input-create-new-tag"
          variant="filled"
          error={errors.tagValue}
          autoComplete="name"
          onSubmitEditing={handleSubmit(handleLinkAndCreateOrEditTag)}
          name="tagValue"
          control={control as unknown as Control}
          placeholder="Digite o nome da tag"
          isReadOnly={loading}
          inputRightElement={
            <Button
              variant={'textLink'}
              isDisabled={!createNewTagIsValid || !!errors.tagValue || loading}
              onPress={handleSubmit(handleLinkAndCreateOrEditTag)}>
              <Text
                variant="semibold"
                fontSize={Typography['fontsize.md']}
                color={inputProps.textColor}>
                {inputProps.textValue}
              </Text>
            </Button>
          }
        />
      )}
      {mode === 'add' && (
        <Flex style={gapWrapperStyle} flexWrap="wrap" w="full" direction="row" bg="white">
          {tagItemList}
        </Flex>
      )}
      {mode === 'edit' && TagEditMode}
    </VStack>
  );
};
